import { useCallback, useState } from 'react';

/**
 * Bump the version suffix whenever the disclosure wording changes so every
 * user acknowledges the new text once more.
 */
export const PARTNER_DISCLOSURE_STORAGE_KEY = 'xmoney:partner-disclosure:v1';

function readAcknowledged(): boolean {
  try {
    return window.localStorage.getItem(PARTNER_DISCLOSURE_STORAGE_KEY) !== null;
  } catch {
    // Storage can be unavailable (private mode, blocked third-party storage,
    // quota errors). Fall back to showing the disclosure.
    return false;
  }
}

function writeAcknowledged(): void {
  try {
    window.localStorage.setItem(
      PARTNER_DISCLOSURE_STORAGE_KEY,
      new Date().toISOString()
    );
  } catch {
    // Best effort only; the in-memory state still dismisses the modal.
  }
}

export interface UsePartnerDisclosureOptions {
  /**
   * Whether the disclosure applies at all. Pass the `openpayd_integration`
   * feature flag here: `false` (or `undefined`, while flags are still
   * loading or failed to load) hides the disclosure for everyone, `true`
   * shows it to anyone who has not acknowledged it yet.
   */
  enabled: boolean | undefined;
}

export interface UsePartnerDisclosureResult {
  /** True when the disclosure should be rendered. */
  isDisclosureRequired: boolean;
  /** Persist the acknowledgement and hide the disclosure. */
  approveDisclosure: () => void;
}

export default function usePartnerDisclosure({
  enabled,
}: UsePartnerDisclosureOptions): UsePartnerDisclosureResult {
  const [isAcknowledged, setIsAcknowledged] = useState(readAcknowledged);

  const approveDisclosure = useCallback(() => {
    writeAcknowledged();
    setIsAcknowledged(true);
  }, []);

  return {
    isDisclosureRequired: enabled === true && !isAcknowledged,
    approveDisclosure,
  };
}
